import React, { useCallback, useMemo } from "react";

import { useIntl } from "react-intl";

import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";

import CreateCompanyModal from "@app/component/createEditEntity/CreateCompanyModal";
import CreateDealModal from "@app/component/createEditEntity/CreateDealModal";
import CreatePersonModal from "@app/component/createEditEntity/CreatePersonModal";
import RelationshipType from "@app/component/preview/enum/RelationshipType";

import IRelationships from "../../type/IRelationships";

interface Props
  extends Omit<
    IRelationships,
    "onSelectDeals" | "onSelectPeople" | "peopleMultiSelect" | "supportedRelationshipTypes"
  > {
  onHide: (type: RelationshipType | undefined) => void;
  onSelectDeals: (ids: Deal["id"][], removedIds?: Deal["id"][]) => void;
  onSelectPeople: (ids: Person["id"][], removedIds?: Person["id"][]) => void;
  relationshipType?: RelationshipType;
}

const AddNewRecordModals: React.FC<Props> = ({
  childCompanies,
  deals,
  entity,
  onHide,
  onSelectChildCompanies,
  onSelectDeals,
  onSelectParentCompanies,
  onSelectParentCompany,
  onSelectPeople,
  parentCompany,
  people,
  primaryCompanyId,
  relationshipType,
}) => {
  const intl = useIntl();

  const assignedCompanies = useMemo(
    () =>
      relationshipType === RelationshipType.CHILD_COMPANY
        ? childCompanies
        : entity.entity === EntityType.PERSON
        ? entity.accounts
        : parentCompany
        ? [parentCompany]
        : undefined,
    [childCompanies, entity, parentCompany, relationshipType]
  );

  const handleHideCreateCompanyModal = useCallback(
    (newCompany?: Company) => {
      if (newCompany) {
        if (
          relationshipType === RelationshipType.PARENT_COMPANY &&
          entity.entity !== EntityType.PERSON
        ) {
          onSelectParentCompany?.({ id: entity.id, parentCompanyId: newCompany.id });
        } else {
          (entity.entity === EntityType.PERSON
            ? onSelectParentCompanies
            : onSelectChildCompanies)?.({
            id: entity.id,
            companyIds: [...(assignedCompanies ?? []).map(({ id }) => id), newCompany.id],
            primaryCompanyId,
          });
        }
      }
      onHide(undefined);
    },
    [
      assignedCompanies,
      entity.entity,
      entity.id,
      onHide,
      onSelectChildCompanies,
      onSelectParentCompanies,
      onSelectParentCompany,
      primaryCompanyId,
      relationshipType,
    ]
  );

  const handleHideCreatePersonModal = useCallback(
    (newPerson?: Person) => {
      if (newPerson && onSelectPeople) {
        onSelectPeople([...(people ?? []).map(({ id }) => id), newPerson.id]);
      }
      onHide(undefined);
    },
    [onHide, onSelectPeople, people]
  );

  const handleHideCreateDealModal = useCallback(
    (newDeal?: Deal) => {
      if (newDeal && onSelectDeals) {
        onSelectDeals([...(deals ?? []).map(({ id }) => id), newDeal.id]);
      }
      onHide(undefined);
    },
    [deals, onHide, onSelectDeals]
  );

  return (
    <>
      {[RelationshipType.CHILD_COMPANY, RelationshipType.PARENT_COMPANY].includes(
        relationshipType as RelationshipType
      ) && (
        <CreateCompanyModal
          fixedParentCompanyId={
            relationshipType === RelationshipType.CHILD_COMPANY ? entity.id : undefined
          }
          okText={intl.formatMessage({
            id: "addNewRecordModals.addCompanyModal.okButton",
            defaultMessage: "Create and Add Company",
            description:
              "Create button title on Create Company modal when called from Relationships Tab",
          })}
          onHide={handleHideCreateCompanyModal}
        />
      )}
      {relationshipType === RelationshipType.PEOPLE && (
        <CreatePersonModal
          fixedCompany={entity.entity === EntityType.COMPANY ? entity : undefined}
          okText={intl.formatMessage({
            id: "addNewRecordModals.addNewPersonModal.okButton",
            defaultMessage: "Create and Add Person",
            description:
              "Create button title on Create Person modal when called from Relationships Tab",
          })}
          onHide={handleHideCreatePersonModal}
        />
      )}
      {relationshipType === RelationshipType.DEALS && (
        <CreateDealModal
          fixedCompany={
            entity.entity === EntityType.COMPANY
              ? entity
              : entity.entity === EntityType.PERSON
              ? ((entity.primaryAccount ?? entity.accounts[0]) as Company | undefined)
              : undefined
          }
          fixedPerson={entity.entity === EntityType.PERSON ? entity : undefined}
          okText={intl.formatMessage({
            id: "addNewRecordModals.addNewDealModal.okButton",
            defaultMessage: "Create and Add Deal",
            description:
              "Create button title on Create Deal modal when called from Relationships Tab",
          })}
          onHide={handleHideCreateDealModal}
        />
      )}
    </>
  );
};

export default AddNewRecordModals;
